import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AppWindow, Download, Hammer, RefreshCw, Layers } from "lucide-react";
import { api, apiError, API_BASE, formatBytes } from "../../lib/api";

const statCls = "rounded-sm border border-[#262930] bg-[#121418] p-5";
const statNum = "font-display text-3xl text-[#F4F1EA]";
const statLbl = "mt-1 text-[10px] uppercase tracking-[0.25em] text-[#6E6B63]";

export const AppTab = ({ meta }) => {
  const [inst, setInst] = useState(null);
  const [manifest, setManifest] = useState(null);

  const loadStatus = useCallback(() => {
    api.get("/admin/installer-status").then((r) => setInst(r.data)).catch(() => {});
    api.get("/pages/manifest").then((r) => setManifest(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (inst?.status === "building") {
      const t = setInterval(loadStatus, 5000);
      return () => clearInterval(t);
    }
  }, [inst?.status, loadStatus]);

  const rebuild = async () => {
    try {
      await api.post("/admin/build-installer");
      toast.success("Windows app build started — it takes a few minutes");
      loadStatus();
    } catch (e) {
      toast.error(apiError(e, "Could not start the build."));
    }
  };

  const building = inst?.status === "building";
  const pagesReady = manifest?.status === "ready";

  return (
    <div className="max-w-2xl">
      <p className="text-sm leading-relaxed text-[#A39E93]">
        The app mirrors the read page exactly — your pages, two at a time, grey on dark, with a fullscreen button and nothing else. The
        universal app file is generated fresh on every download; the Windows app is rebuilt on demand with the button below.
      </p>

      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <div className={statCls}>
          <p className={statNum}>{pagesReady ? manifest.count : "—"}</p>
          <p className={statLbl}>Pages inside</p>
        </div>
        <div className={statCls}>
          <Layers className="mt-1 h-7 w-7 text-[#D4A373]" />
          <p className={statLbl}>Fixed-layout pages</p>
        </div>
        <div className={statCls}>
          <AppWindow className="mt-1 h-7 w-7 text-[#D4A373]" />
          <p className={statLbl}>Own window, offline</p>
        </div>
      </div>

      <a
        href={`${API_BASE}/download/app`}
        data-testid="admin-download-app-button"
        className="mt-8 inline-flex items-center gap-3 rounded-full border border-[#3E4350] px-7 py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EA] transition-colors hover:border-[#D4A373] hover:text-[#D4A373]"
      >
        <Download className="h-4 w-4" />
        Download the app file
      </a>

      <div className="mt-12 border-t border-[#262930] pt-10">
        <h2 className="font-display text-2xl text-[#F4F1EA]">Windows app (.exe)</h2>
        <p className="mt-3 text-sm leading-relaxed text-[#A39E93]">
          A real Windows program: readers double-click the .exe and the book opens in its own window — no installation wizard. When online
          it always shows your newest pages; offline it reads the pages packaged inside.
        </p>

        <div className="mt-6 rounded-sm border border-[#262930] bg-[#121418] p-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#6E6B63]">Status</p>
          <p data-testid="installer-status" className="mt-2 font-display text-xl text-[#F4F1EA]">
            {inst === null && "Checking…"}
            {inst?.status === "none" && "Not built yet"}
            {inst?.status === "building" && "Building — this takes a few minutes…"}
            {inst?.status === "ready" && `Ready · built ${new Date(inst.built_at).toLocaleDateString()} · ${formatBytes(inst.size)}`}
            {inst?.status === "failed" && "Last build failed"}
          </p>
          {inst?.status === "failed" && inst.error && <p className="mt-2 break-words font-mono text-xs text-[#C85A32]">{inst.error}</p>}
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              data-testid="admin-build-installer-button"
              onClick={rebuild}
              disabled={building || !pagesReady}
              className="inline-flex items-center gap-2 rounded-full bg-[#D4A373] px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0B0C0E] transition-colors hover:bg-[#E0A96D] disabled:opacity-50"
            >
              {building ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Hammer className="h-3.5 w-3.5" />}
              {building ? "Building…" : inst?.status === "ready" ? "Rebuild" : "Build Windows app"}
            </button>
            {inst?.status === "ready" && (
              <a
                href={`${API_BASE}/download/app-windows`}
                data-testid="admin-download-installer-button"
                className="inline-flex items-center gap-2 rounded-full border border-[#3E4350] px-6 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#A39E93] transition-colors hover:border-[#D4A373] hover:text-[#D4A373]"
              >
                <Download className="h-3.5 w-3.5" />
                Download .exe
              </a>
            )}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-[#6E6B63]">
            {!pagesReady
              ? "The Windows app needs your rendered pages first — upload the .odt on the Book file tab."
              : "Rebuild after uploading a revised .odt, and once more after your live site is deployed."}
          </p>
        </div>
      </div>
    </div>
  );
};
